import { cn } from '@elara/client-core/lib/utils';

import { Button, Card } from '#ui';

import { FLOATING_CARD_CLASS, FLOATING_CARD_STYLE } from './floating-card.js';

export interface NotificationOfferPromptProps {
  /** `ask`: the browser can still prompt. `blocked`: notifications are blocked, so point to settings. */
  offer: 'ask' | 'blocked';
  /** Disables the actions while the browser's own permission prompt is open. */
  isBusy?: boolean;
  /** "Notify me" (ask) or "Turn on" (blocked). */
  onAccept: () => void;
  onDismiss: () => void;
  className?: string;
}

const COPY = {
  ask: { message: 'This might take a minute. Get notified when it’s ready?', accept: 'Notify me' },
  blocked: { message: 'Notifications are blocked for Elara. Turn them on?', accept: 'Turn on' },
} as const;

/**
 * Offers notifications while a reply is taking a while, in the same floating slot above the composer
 * as the "remember this?" prompts. `ChatNotificationOffer` decides when it shows.
 */
export function NotificationOfferPrompt({ offer, isBusy = false, onAccept, onDismiss, className }: NotificationOfferPromptProps) {
  const copy = COPY[offer];
  return (
    <Card
      role="region"
      aria-label="Get notified when Elara replies"
      className={cn(FLOATING_CARD_CLASS, 'flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3', className)}
      style={FLOATING_CARD_STYLE}
    >
      <p className="min-w-0 text-sm sm:flex-1">{copy.message}</p>
      <div className="ml-auto flex flex-shrink-0 items-center gap-1">
        <Button variant="ghost" size="sm" disabled={isBusy} onClick={onDismiss}>
          Not now
        </Button>
        <Button variant="default" size="sm" isBusy={isBusy} disabled={isBusy} onClick={onAccept}>
          {copy.accept}
        </Button>
      </div>
    </Card>
  );
}
